import { inject, Injectable } from '@angular/core';
import { AllDocTypes, DocTypeDefinitions, DocTypesNumToName } from '../Interfaces/SQL-morning-responses';
import {
	DEFAULT_RESERVED_ORDER_DOCTYPE,
	DEFAULT_RETURN_DOCTYPE,
	SERVICE_CALL_DOCTYPE_TECHNOTIRE,
	SPECIAL_REFUND_DOCTYPES,
	SPECIAL_RESERVED_ORDER_DOCTYPES,
	VAN_SALE_DOCTYPES,
} from '../Utility/constants-doctype';
import { StoreService } from './store.service';

export const doctypeOptions = {
	0: 'order',
	1: 'return',
	2: 'virtual',
} as const;

export const dummyDoctype: AllDocTypes = {
	CheckMinPrice: 1,
	NoObligoCheck: 0,
	OpenBodd: 0,
	MustCustRem1: 0,
	MustCustRem2: 0,
	MustCustRem3: 0,
	MustCustRem4: 0,
	AgentID: '',
	ClientID: '',
	DocType: 0,
	DocName: 'בדיקה',
	IsShrion: 0,
	Company: 1,
	Show: 1,
};

@Injectable({
	providedIn: 'root',
})
export class DoctypeService {
	private _ss = inject(StoreService);
	private doctypeNamesRecord: Record<number, string> = {};
	private doctypesPerClient: Record<string, number[]> = {};

	doctypesSet: Set<number> = new Set();
	doctypesDefinitions: DocTypeDefinitions[] = [];
	hasExtraDoctypeDefinitions = false;
	typesOfDoctypes: { [P in typeof doctypeOptions[keyof typeof doctypeOptions]]: Set<number> } = {
		order: new Set(),
		return: new Set(),
		virtual: new Set(),
	};

	constructor() {}

	buildDoctypeNamesRecord(doctypes: AllDocTypes[]): void {
		if (!Object.keys(this.doctypesPerClient).length) {
			this.buildDoctypeNamesAndDoctypesPerClient(doctypes);
			return;
		}
		this.doctypeNamesRecord = doctypes.reduce((acc, doctype) => {
			this.doctypesSet.add(doctype.DocType);
			acc[doctype.DocType] = doctype.DocName;
			return acc;
		}, {});
	}

	buildDoctypesPerClient(doctypes: AllDocTypes[]): void {
		if (!Object.keys(this.doctypeNamesRecord).length) {
			this.buildDoctypeNamesAndDoctypesPerClient(doctypes);
			return;
		}
		this.doctypesPerClient = doctypes.reduce((acc, doctype) => {
			this.doctypesSet.add(doctype.DocType);
			acc[doctype.ClientID] ||= [];
			acc[doctype.ClientID].push(doctype.DocType);
			return acc;
		}, {});
	}

	buildDoctypeNamesAndDoctypesPerClient(doctypes: AllDocTypes[]): void {
		for (const doctype of doctypes) {
			this.doctypesSet.add(doctype.DocType);
			this.doctypeNamesRecord[doctype.DocType] = doctype.DocName;
			this.doctypesPerClient[doctype.ClientID] ||= [];
			this.doctypesPerClient[doctype.ClientID].push(doctype.DocType);
		}
	}

	buildDoctypesDefinitions(): void {
		this.hasExtraDoctypeDefinitions = true;
		if (!this.doctypesDefinitions.length || !('Typeof' in this.doctypesDefinitions[0])) {
			this.hasExtraDoctypeDefinitions = false;
			return;
		}
		for (const doctype of this.doctypesDefinitions) {
			this.typesOfDoctypes[doctypeOptions[doctype.Typeof]].add(doctype.DocType);
		}
	}

	getDoctypesPerClient(clientId: string): DocTypesNumToName[] {
		return this.doctypesPerClient[clientId].map(doctype => ({ DocType: doctype, DocTypeName: this.doctypeNamesRecord[doctype] }));
	}

	getDoctypeName(doctypeId: number): string | undefined {
		return this.doctypeNamesRecord[doctypeId];
	}

	setDoctypeName(doctypeId: number, doctypeName: string): void {
		this.doctypeNamesRecord[doctypeId] = doctypeName;
	}

	resetDoctypeNames(): void {
		this.doctypeNamesRecord = {};
	}

	resetDoctypesPerClient(): void {
		this.doctypesPerClient = {};
	}

	getAllDoctypeNames(doctypes: number[]): { doctype: number; name: string }[] {
		return doctypes.map(doctype => ({ doctype, name: this.getDoctypeName(doctype) }));
	}

	isDoctypeOfTypeReturnOrRefund(doc: string | number = +this._ss.DocType): boolean {
		if (!this.doesHaveExtraDoctypeDefinitions()) return this.isSpecialRefundDoctype(doc) || +doc === DEFAULT_RETURN_DOCTYPE;
		return this.isSpecialRefundDoctype(doc) || this.typesOfDoctypes.return.has(+doc);
	}

	doesHaveExtraDoctypeDefinitions(): boolean {
		return this.hasExtraDoctypeDefinitions;
	}

	getAllDoctypes(): number[] {
		return [...this.doctypesSet];
	}

	isSpecialRefundDoctype(doctype: number | string) {
		return SPECIAL_REFUND_DOCTYPES.has(+doctype);
	}

	isVanSaleDoctype(doctype: number | string) {
		return VAN_SALE_DOCTYPES.has(+doctype);
	}

	isSpecialReservedDoctype(doctype: number | string): boolean {
		return SPECIAL_RESERVED_ORDER_DOCTYPES.has(+doctype) || +doctype === DEFAULT_RESERVED_ORDER_DOCTYPE;
	}

	isTechnotireServiceCallDoctype(doctype: number | string): boolean {
		return SERVICE_CALL_DOCTYPE_TECHNOTIRE === +doctype;
	}
}
